import React, { useEffect, useMemo, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  Circle } from 'react-leaflet';
import { Box,
  Text,
  Spinner,
  Center,
  VStack,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { FaMapMarkerAlt, FaSearchLocation } from 'react-icons/fa';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cartoTileUrl } from './cartoTiles';

// Icono personalizado moderno para el marcador
const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-marker-icon',
    html: `
      <div style="
        position: relative;
        width: 40px;
        height: 52px;
        filter: drop-shadow(0 4px 8px rgba(139, 92, 246, 0.35));
      ">
        <svg width="40" height="52" viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0C8.954 0 0 8.954 0 20C0 35 20 52 20 52C20 52 40 35 40 20C40 8.954 31.046 0 20 0Z" fill="url(#gradient)"/>
          <circle cx="20" cy="20" r="8" fill="white"/>
          <circle cx="20" cy="20" r="4" fill="#8B5CF6"/>
          <defs>
            <linearGradient id="gradient" x1="20" y1="0" x2="20" y2="52" gradientUnits="userSpaceOnUse">
              <stop stop-color="#A78BFA"/>
              <stop offset="1" stop-color="#7C3AED"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
    `,
    iconSize: [40, 52],
    iconAnchor: [20, 52],
    popupAnchor: [0, -52],
  });
};

// Componente para actualizar la vista del mapa cuando cambian las coordenadas
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }
  }, [center, zoom, map]);
  
  return null;
};

class LeafletBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

// Estilos de mapa disponibles (todos gratuitos sin API key)
const MAP_STYLES = {
  // CartoDB Voyager - Estilo moderno y limpio
  voyager: {
    url: cartoTileUrl('rastertiles/voyager'),
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  // CartoDB Positron - Estilo minimalista claro
  positron: {
    url: cartoTileUrl('light_all'),
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  // CartoDB Dark Matter - Estilo oscuro elegante
  dark: {
    url: cartoTileUrl('dark_all'),
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  // Stadia Alidade Smooth - Muy moderno
  smooth: {
    url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  }
};

const LocationMap = ({ 
  address, // { calle, numero, ciudad, provincia, codigoPostal, pais }
  height = "250px",
  showPopup = true,
  nombre = "Ubicación",
  mapStyle = "voyager", // voyager, positron, dark, smooth
  showPulse = true
}) => {
  const [coordinates, setCoordinates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExact, setIsExact] = useState(false);

  const customIcon = useMemo(() => createCustomIcon(), []);
  const selectedStyle = MAP_STYLES[mapStyle] || MAP_STYLES.voyager;

  // Construir la dirección completa para geocodificar
  const getFullAddress = () => {
    if (!address) return null;
    
    const parts = [];
    if (address.calle) {
      parts.push(address.calle);
      if (address.numero) parts[0] += ` ${address.numero}`;
    }
    if (address.ciudad) parts.push(address.ciudad);
    if (address.provincia) parts.push(address.provincia);
    if (address.codigoPostal) parts.push(address.codigoPostal);
    parts.push(address.pais || 'España');
    
    return parts.length > 1 ? parts.join(', ') : null;
  };

  const getShortAddress = () => {
    if (!address) return '';
    const parts = [];
    if (address.calle) parts.push(address.calle);
    if (address.ciudad) parts.push(address.ciudad);
    return parts.join(', ');
  };

  // Geocodificar la dirección usando Nominatim (OpenStreetMap)
  useEffect(() => {
    const geocodeAddress = async () => {
      const fullAddress = getFullAddress();
      
      if (!fullAddress) {
        setCoordinates(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
          {
            headers: {
              'User-Agent': 'Cadencya/1.0'
            }
          }
        );

        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          
          // Validar que las coordenadas son números válidos
          if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
            setCoordinates({
              lat,
              lng,
              displayName: data[0].display_name
            });
            setIsExact(true);
          } else {
            setError('Coordenadas inválidas recibidas');
            setCoordinates(null);
          }
        } else {
          // Si no encuentra la dirección exacta, intentar solo con ciudad y provincia
          const fallbackAddress = [address.ciudad, address.provincia, 'España'].filter(Boolean).join(', ');
          const fallbackResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackAddress)}&limit=1`,
            {
              headers: {
                'User-Agent': 'Cadencya/1.0'
              }
            }
          );
          const fallbackData = await fallbackResponse.json();
          
          if (fallbackData && fallbackData.length > 0) {
            const lat = parseFloat(fallbackData[0].lat);
            const lng = parseFloat(fallbackData[0].lon);
            
            // Validar que las coordenadas son números válidos
            if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
              setCoordinates({
                lat,
                lng,
                displayName: fallbackData[0].display_name
              });
              setIsExact(false);
            } else {
              setError('Coordenadas inválidas recibidas');
              setCoordinates(null);
            }
          } else {
            setError('No se pudo encontrar la ubicación');
            setCoordinates(null);
          }
        }
      } catch (err) {
        console.error('Error geocodificando:', err);
        setError('Error al buscar la ubicación');
        setCoordinates(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce para evitar muchas peticiones
    const timeoutId = setTimeout(geocodeAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [address?.calle, address?.numero, address?.ciudad, address?.provincia, address?.codigoPostal]);

  // Verificar si hay suficiente información de dirección
  const hasMinimumAddress = address?.ciudad && address?.provincia;
  
  // Verificar si las coordenadas son válidas
  const hasValidCoordinates = coordinates && 
    !isNaN(coordinates.lat) && 
    !isNaN(coordinates.lng) && 
    isFinite(coordinates.lat) && 
    isFinite(coordinates.lng);

  if (!hasMinimumAddress) {
    return (
      <Box
        h={height}
        bg="linear-gradient(135deg, #f8f9fc 0%, #eef1f5 100%)"
        rounded="2xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px dashed"
        borderColor="gray.300"
        position="relative"
        overflow="hidden"
      >
        <VStack gap={3}>
          <Box
            p={4}
            bg="white"
            rounded="full"
            shadow="lg"
          >
            <Icon boxSize={8} color="purple.400" asChild><FaSearchLocation /></Icon>
          </Box>
          <Text color="gray.600" fontSize="sm" textAlign="center" px={4} fontWeight="medium">
            Añade ciudad y provincia para ver el mapa
          </Text>
        </VStack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        h={height}
        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        rounded="2xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.200"
        />
        <Center zIndex={1}>
          <VStack gap={3}>
            <Spinner size="xl" color="white" borderWidth="4px" animationDuration="0.8s" />
            <Text color="white" fontSize="sm" fontWeight="medium">
              Buscando ubicación...
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (error || !coordinates || !hasValidCoordinates) {
    return (
      <Box
        h={height}
        bg="linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)"
        rounded="2xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px dashed"
        borderColor="red.200"
      >
        <VStack gap={3}>
          <Box p={4} bg="red.50" rounded="full">
            <Icon boxSize={8} color="red.400" asChild><FaMapMarkerAlt /></Icon>
          </Box>
          <Text color="gray.600" fontSize="sm" textAlign="center" px={4}>
            {error || 'No se pudo cargar el mapa'}
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      h={height}
      rounded="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      position="relative"
      shadow="lg"
      css={{
        '& .leaflet-container': {
          borderRadius: '1rem',
        },

        '& .leaflet-popup-content-wrapper': {
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        },

        '& .leaflet-popup-content': {
          margin: '12px 16px',
        },

        '& .leaflet-popup-tip': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },

        '& @keyframes pulse-ring': {
          '0%': { transform: 'scale(0.5)', opacity: 1 },
          '100%': { transform: 'scale(2)', opacity: 0 },
        }
      }}
    >
      <LeafletBoundary
        fallback={
          <Center h="100%" bg="gray.50">
            <Text fontSize="sm" color="gray.600">
              No se pudo cargar el mapa
            </Text>
          </Center>
        }
      >
      <MapContainer
        key={`${coordinates.lat},${coordinates.lng}`}
        center={[coordinates.lat, coordinates.lng]}
        zoom={isExact ? 16 : 13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={false}
      >
        <TileLayer
          attribution={selectedStyle.attribution}
          url={selectedStyle.url}
        />
        
        {/* Círculo de pulso animado */}
        {showPulse && (
          <Circle
            center={[coordinates.lat, coordinates.lng]}
            radius={isExact ? 50 : 500}
            pathOptions={{
              color: '#8B5CF6',
              fillColor: '#8B5CF6',
              fillOpacity: 0.1,
              weight: 2,
              opacity: 0.3,
            }}
          />
        )}
        
        <Marker 
          position={[coordinates.lat, coordinates.lng]}
          icon={customIcon}
        >
          {showPopup && (
            <Popup>
              <div>
                <strong>{nombre}</strong>
                <div>{getShortAddress()}</div>
              </div>
            </Popup>
          )}
        </Marker>
        <MapUpdater center={[coordinates.lat, coordinates.lng]} zoom={isExact ? 16 : 13} />
      </MapContainer>
      </LeafletBoundary>

      {/* Badge de ubicación */}
      <Box
        position="absolute"
        bottom={3}
        left={3}
        bg="white"
        px={3}
        py={1.5}
        rounded="full"
        shadow="md"
        zIndex={1000}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Box
          w={2}
          h={2}
          rounded="full"
          bg={isExact ? "green.400" : "orange.400"}
        />
        <Text fontSize="xs" fontWeight="medium" color="gray.700">
          {isExact ? 'Ubicación exacta' : 'Zona aproximada'}
        </Text>
      </Box>
    </Box>
  );
};

export default LocationMap;
